import { Combobox } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOM_CHART_BAR_ORIENTATIONS,
  type ChartCategoryColors,
  type ChartValueColorRule,
  type CustomChartBarOrientation,
  type CustomChartColor,
  type CustomChartType,
} from '@/entities/dashboards';
import { TruncatedText } from '@/shared/ui';
import { ChartColorPicker } from './ChartColorPicker';
import { ChartValueColorRulesField } from './ChartValueColorRulesField';

type Props = {
  chartType: CustomChartType;
  barOrientation: CustomChartBarOrientation;
  onChangeBarOrientation: (orientation: CustomChartBarOrientation) => void;
  /** Категории последней полученной выборки — им и задаются цвета */
  categories: string[];
  categoryColors: ChartCategoryColors;
  onChangeCategoryColors: (colors: ChartCategoryColors) => void;
  valueColorRules: ChartValueColorRule[];
  onChangeValueColorRules: (rules: ChartValueColorRule[]) => void;
};

/** Типы, у которых цвет означает категорию, а не величину: им и задаются цвета категорий. */
const CATEGORY_COLOR_CHART_TYPES: CustomChartType[] = ['bar', 'pie'];

/**
 * Типы, у которых величина нарисована или показана числом: их и красят правила величины.
 *
 * Круговой диаграммы в перечне нет — там цвет означает категорию; нет и типов, показывающих
 * результат текстом: красить в них нечего.
 */
const VALUE_RULE_CHART_TYPES: CustomChartType[] = [
  'bar',
  'line',
  'heatmap',
  'table',
  'grouped-table',
  'selection-breakdown',
];

/** Подписи направлений элементов — ключи перевода по коду направления. */
const BAR_ORIENTATION_LABEL_KEY: Record<
  CustomChartBarOrientation,
  'presets.editor.barOrientations.horizontal' | 'presets.editor.barOrientations.vertical'
> = {
  horizontal: 'presets.editor.barOrientations.horizontal',
  vertical: 'presets.editor.barOrientations.vertical',
};

/**
 * Оформление диаграммы в её свойствах: направление элементов, цвета категорий и правила раскраски
 * по величине.
 *
 * Поля показаны там, где им есть что задавать: направление — только у столбчатой диаграммы, цвета
 * категорий — там, где цвет означает категорию, правила величины — везде, кроме круговой.
 *
 * Заданное применяется к диаграмме сразу, ещё до сохранения свойств: цвет и направление выбирают,
 * глядя на саму диаграмму, а не вслепую.
 */
export const ChartAppearanceFields: FC<Props> = ({
  chartType,
  barOrientation,
  onChangeBarOrientation,
  categories,
  categoryColors,
  onChangeCategoryColors,
  valueColorRules,
  onChangeValueColorRules,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const orientationOptions = useMemo(
    () =>
      CUSTOM_CHART_BAR_ORIENTATIONS.map((orientation) => ({
        label: t(BAR_ORIENTATION_LABEL_KEY[orientation]),
        value: orientation,
      })),
    [t],
  );

  // Категория, которой цвет задан, остаётся в перечне и после того, как выпала из выборки: цвет
  // за ней сохранён, и снять его иначе было бы негде.
  const coloredCategories = useMemo(() => {
    const named = Object.keys(categoryColors).filter((label) => !categories.includes(label));
    return [...categories, ...named];
  }, [categories, categoryColors]);

  const isOrientationShown = chartType === 'bar';
  const isCategoryColorsShown = CATEGORY_COLOR_CHART_TYPES.includes(chartType);
  const isValueRulesShown = VALUE_RULE_CHART_TYPES.includes(chartType);

  if (!isOrientationShown && !isCategoryColorsShown && !isValueRulesShown) return null;

  const selectOrientation = (value: unknown) =>
    onChangeBarOrientation((value as CustomChartBarOrientation | undefined) ?? barOrientation);

  const setCategoryColor = (label: string, color: CustomChartColor | undefined) => {
    const next = { ...categoryColors };
    // Снятый цвет убирается из состава, а не остаётся пустым значением: категория без записи
    // красится набором по своему порядку.
    if (color) next[label] = color;
    else delete next[label];
    onChangeCategoryColors(next);
  };

  return (
    <div className="flex flex-col gap-4 border-t pt-4">
      <p className="text-sm font-medium">{t('presets.editor.appearance')}</p>

      {isOrientationShown && (
        <Combobox
          label={t('presets.editor.barOrientation')}
          value={barOrientation}
          onChange={selectOrientation}
          options={orientationOptions}
          isSearchable={false}
          className="w-full @md/chart-properties:w-1/2"
        />
      )}

      {isCategoryColorsShown && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">{t('presets.editor.categoryColors')}</p>
          {coloredCategories.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('presets.editor.categoryColorsEmpty')}</p>
          ) : (
            coloredCategories.map((label) => (
              <div key={label} className="flex flex-wrap items-center gap-3">
                {/* Место у названия ограничено: не поместившееся раскрывается подсказкой целиком */}
                <TruncatedText className="max-w-60 text-sm">{label}</TruncatedText>
                <ChartColorPicker
                  value={categoryColors[label]}
                  onChange={(color) => setCategoryColor(label, color)}
                  isResettable
                />
              </div>
            ))
          )}
        </div>
      )}

      {isValueRulesShown && <ChartValueColorRulesField rules={valueColorRules} onChange={onChangeValueColorRules} />}
    </div>
  );
};
